import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Wifi,
  WifiOff,
  RefreshCw,
  Database,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  Server,
  BarChart3,
  FileText,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import { api } from '@/services/api';

interface OfflineStatus {
  isOnline: boolean;
  connectionType: 'wifi' | 'cellular' | 'none';
  quality: 'excellent' | 'good' | 'poor' | 'none';
  latency: number;
  lastChecked: string;
}

interface OfflineMetrics {
  totalOperations: number;
  pendingOperations: number;
  syncedOperations: number;
  failedOperations: number;
  averageSyncTime: number;
  lastSyncDuration: number;
  storageUsed: number;
  storageQuota: number;
  uptime: number;
  downtime: number;
}

interface OfflineOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  entity: 'sales_invoice' | 'product' | 'customer' | 'supplier' | 'informal_transaction';
  entityId: string;
  data: any;
  timestamp: string;
  priority: number;
  retryCount: number;
  maxRetries: number;
  syncStatus: 'pending' | 'syncing' | 'synced' | 'failed' | 'conflict';
  lastSyncAttempt?: string;
  errorMessage?: string;
}

interface SyncSession {
  id: string;
  startTime: string;
  endTime?: string;
  status: 'in_progress' | 'completed' | 'failed' | 'partial';
  operationsAttempted: number;
  operationsSuccessful: number;
  operationsFailed: number;
  bytesTransferred: number;
  errorDetails?: string;
}

const OfflineEnginePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'status' | 'operations' | 'sessions' | 'metrics'>('status');
  const queryClient = useQueryClient();

  // Fetch offline status
  const { data: statusData } = useQuery<{
    connectivity: OfflineStatus;
    metrics: OfflineMetrics;
    deviceInfo: any;
    isOnline: boolean;
  }>({
    queryKey: ['offline-engine', 'status'],
    queryFn: async () => {
      const res = await api.get('/api/offline-engine/status');
      return res.data.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch operations
  const { data: operationsData } = useQuery<{
    operations: OfflineOperation[];
    total: number;
    pending: number;
    synced: number;
    failed: number;
  }>({
    queryKey: ['offline-engine', 'operations'],
    queryFn: async () => {
      const res = await api.get('/api/offline-engine/operations');
      return res.data.data;
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Fetch sync sessions
  const { data: sessionsData } = useQuery<{
    sessions: SyncSession[];
    count: number;
  }>({
    queryKey: ['offline-engine', 'sync-sessions'],
    queryFn: async () => {
      const res = await api.get('/api/offline-engine/sync-sessions');
      return res.data.data;
    },
  });

  // Fetch metrics
  const { data: metricsData } = useQuery<OfflineMetrics>({
    queryKey: ['offline-engine', 'metrics'],
    queryFn: async () => {
      const res = await api.get('/api/offline-engine/metrics');
      return res.data.data;
    },
    refetchInterval: 15000, // Refresh every 15 seconds
  });

  // Manual sync mutation
  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/api/offline-engine/sync');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['offline-engine'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to start sync');
    },
  });

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
    return `${(ms / 3600000).toFixed(1)}h`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getSyncStatusColor = (status: string) => {
    switch (status) {
      case 'synced':
        return 'bg-green-100 text-green-800';
      case 'syncing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'conflict':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSyncStatusIcon = (status: string) => {
    switch (status) {
      case 'synced':
        return <CheckCircle className="h-4 w-4" />;
      case 'syncing':
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'failed':
        return <AlertTriangle className="h-4 w-4" />;
      case 'conflict':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getConnectionQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent':
        return 'bg-green-100 text-green-800';
      case 'good':
        return 'bg-blue-100 text-blue-800';
      case 'poor':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Offline Engine</h1>
          <p className="mt-1 text-sm text-gray-600">
            Monitor and manage offline operations and synchronization
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending || !statusData?.isOnline}
            variant="primary"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {syncMutation.isPending ? 'Syncing...' : 'Start Sync'}
          </Button>
        </div>
      </div>

      {/* Status Overview */}
      {statusData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Connectivity</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statusData.isOnline ? 'Online' : 'Offline'}
                </p>
                <div className="flex items-center mt-2">
                  {statusData.isOnline ? (
                    <Wifi className="h-4 w-4 text-green-600 mr-2" />
                  ) : (
                    <WifiOff className="h-4 w-4 text-red-600 mr-2" />
                  )}
                  <span className={`text-sm ${getSyncStatusColor(statusData.connectivity.quality)}`}>
                    {statusData.connectivity.connectionType}
                  </span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${statusData.isOnline ? 'bg-green-100' : 'bg-red-100'}`}>
                {statusData.isOnline ? (
                  <Wifi className="h-6 w-6 text-green-600" />
                ) : (
                  <WifiOff className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Operations</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statusData.metrics.pendingOperations}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Total: {statusData.metrics.totalOperations}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Database className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Sync Success Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statusData.metrics.totalOperations > 0 
                    ? Math.round((statusData.metrics.syncedOperations / statusData.metrics.totalOperations) * 100)
                    : 0}%
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {statusData.metrics.syncedOperations} synced
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Storage Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatBytes(statusData.metrics.storageUsed)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  of {formatBytes(statusData.metrics.storageQuota)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Server className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'status', label: 'Status', icon: Activity },
              { id: 'operations', label: 'Operations', icon: Database },
              { id: 'sessions', label: 'Sync Sessions', icon: FileText },
              { id: 'metrics', label: 'Metrics', icon: BarChart3 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2 inline" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'status' && statusData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Connection Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        statusData.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {statusData.isOnline ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Type</span>
                      <span className="text-sm font-medium">{statusData.connectivity.connectionType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Quality</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getConnectionQualityColor(statusData.connectivity.quality)}`}>
                        {statusData.connectivity.quality}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Latency</span>
                      <span className="text-sm font-medium">{statusData.connectivity.latency}ms</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Device Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Device ID</span>
                      <span className="text-sm font-medium">{statusData.deviceInfo.deviceId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Device Name</span>
                      <span className="text-sm font-medium">{statusData.deviceInfo.deviceName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Platform</span>
                      <span className="text-sm font-medium">{statusData.deviceInfo.platform}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Version</span>
                      <span className="text-sm font-medium">{statusData.deviceInfo.version}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-600">Average Sync Time</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatDuration(statusData.metrics.averageSyncTime)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Last Sync Duration</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatDuration(statusData.metrics.lastSyncDuration)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Uptime</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statusData.metrics.uptime.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'operations' && operationsData && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retries</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {operationsData.operations.map((operation) => (
                    <tr key={operation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          operation.type === 'create' ? 'bg-green-100 text-green-800' :
                          operation.type === 'update' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {operation.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {operation.entity.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getSyncStatusIcon(operation.syncStatus)}
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSyncStatusColor(operation.syncStatus)}`}>
                            {operation.syncStatus}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          operation.priority === 4 ? 'bg-red-100 text-red-800' :
                          operation.priority === 3 ? 'bg-orange-100 text-orange-800' :
                          operation.priority === 2 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {operation.priority === 4 ? 'Critical' :
                           operation.priority === 3 ? 'High' :
                           operation.priority === 2 ? 'Normal' : 'Low'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {operation.retryCount}/{operation.maxRetries}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(operation.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'sessions' && sessionsData && (
            <div className="space-y-4">
              {sessionsData.sessions.map((session) => (
                <div key={session.id} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Sync Session - {new Date(session.startTime).toLocaleString()}
                      </h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        session.status === 'completed' ? 'bg-green-100 text-green-800' :
                        session.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        session.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {session.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Operations Attempted</p>
                      <p className="text-xl font-bold text-gray-900">{session.operationsAttempted}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Successful</p>
                      <p className="text-xl font-bold text-green-600">{session.operationsSuccessful}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Failed</p>
                      <p className="text-xl font-bold text-red-600">{session.operationsFailed}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Data Transferred</p>
                      <p className="text-xl font-bold text-gray-900">{formatBytes(session.bytesTransferred)}</p>
                    </div>
                  </div>
                  {session.errorDetails && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-800">{session.errorDetails}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'metrics' && metricsData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Operation Statistics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Operations</span>
                      <span className="text-sm font-medium">{metricsData.totalOperations}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Pending</span>
                      <span className="text-sm font-medium">{metricsData.pendingOperations}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Synced</span>
                      <span className="text-sm font-medium">{metricsData.syncedOperations}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Failed</span>
                      <span className="text-sm font-medium">{metricsData.failedOperations}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Performance</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Average Sync Time</span>
                      <span className="text-sm font-medium">{formatDuration(metricsData.averageSyncTime)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Last Sync Duration</span>
                      <span className="text-sm font-medium">{formatDuration(metricsData.lastSyncDuration)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Storage Used</span>
                      <span className="text-sm font-medium">{formatBytes(metricsData.storageUsed)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Storage Quota</span>
                      <span className="text-sm font-medium">{formatBytes(metricsData.storageQuota)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reliability</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600">Uptime</p>
                    <p className="text-3xl font-bold text-green-600">{metricsData.uptime.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Downtime</p>
                    <p className="text-3xl font-bold text-red-600">{metricsData.downtime.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfflineEnginePage;
